import { useEffect, useState } from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { LoginWindow } from "../../components/LoginWindow";
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ConfirmDialog from "../../components/ConfirmDialog";
import { deleteCurrentUser, deleteRegistration } from "../../utils/client";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/AlertProvider";
import { useUserData } from "../../components/DataProvider";
import { RegistrationSummary, ValidationError } from "baptize-america-client";
import TopNavBar from "../../components/TopNavBar";
import { humaneReadableTime } from "../../utils/helper";
import { signOut } from "aws-amplify/auth";

const styles = {
  mainBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
  },
  innerBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  divider: {
    mt: 4,
    width: "100%",
  },
  actionButton: {
    height: 60,
    width: 300,
  },
  logoutButton: {
    marginBottom: "2rem",
    marginTop: "5rem",
    backgroundColor: "#00169E",
    color: "#FFF",
  },
  deleteButton: {
    backgroundColor: "#d32f2f",
    color: "#FFF",
  },
};

const AccountPage = () => {
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const { userRegistrations, setUserRegistrations, refreshRegistrations } =
    useUserData();
  const [unregisterDialog, setUnregisterDialog] =
    useState<RegistrationSummary>();
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { attributes } = useUserData();

  useEffect(() => {
    refreshRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnregisterForBaptism = async (event: RegistrationSummary) => {
    const sub = attributes.sub;
    deleteRegistration({
      churchId: event.church!!.churchId,
      baptismId: event.baptism!!.baptismId,
      sub,
    }).then(
      () => {
        setUserRegistrations((prevState) => {
          prevState.delete(event.baptism!!.baptismId!!);
          return prevState;
        });
        setAlert({
          showAlert: true,
          alertMsg: `Unregistered for baptism at ${event.church?.name}`,
          alertType: "warning",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error unregistering for baptism",
          alertType: "error",
        });
      },
    );
  };
  const handleDeleteCurrentUser = async () => {
    try {
      await deleteCurrentUser();
      await signOut();
    } catch (err) {
      setAlert({
        showAlert: true,
        alertMsg: "Error deleting current user",
        alertType: "error",
      });
    }
  };
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/churches", { replace: true });
    } catch (err) {
      setAlert({
        showAlert: true,
        alertMsg: "Error signing out user",
        alertType: "error",
      });
    }
  };
  return authStatus === "authenticated" ? (
    <>
      <Container maxWidth={"md"}>
        <Box sx={styles.mainBox} component="form" noValidate autoComplete="off">
          <TopNavBar showBackArrow={false} />
          <Divider
            orientation="horizontal"
            textAlign="left"
            sx={[styles.divider]}
          >
            <Typography variant="body2" gutterBottom sx={{ display: "block" }}>
              User Profile
              <Tooltip
                enterTouchDelay={20}
                leaveTouchDelay={3000}
                title="This information cannot be updated at this time"
              >
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Typography>
          </Divider>
          <Box sx={[styles.innerBox]}>
            <TextField
              variant="standard"
              label="First Name"
              slotProps={{
                input: {
                  readOnly: true,
                },
                inputLabel: {
                  shrink: true,
                },
              }}
              focused={false}
              value={attributes.given_name}
              sx={{ flex: 0.45 }}
            />
            <TextField
              variant="standard"
              id="outlined-required"
              label="Last Name"
              sx={{ flex: 0.45 }}
              slotProps={{
                input: {
                  readOnly: true,
                },
                inputLabel: {
                  shrink: true,
                },
              }}
              focused={false}
              value={attributes.family_name}
            />
          </Box>
          <TextField
            variant="standard"
            label="Email"
            slotProps={{
              input: {
                readOnly: true,
              },
              inputLabel: {
                shrink: true,
              },
            }}
            focused={false}
            value={attributes.email}
            sx={{ mt: 2 }}
            fullWidth
          />
          {userRegistrations.size > 0 && (
            <Divider
              orientation="horizontal"
              textAlign="left"
              sx={[styles.divider]}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ display: "block" }}
              >
                Baptisms
              </Typography>
            </Divider>
          )}
          {[...userRegistrations.entries()].map(([key, value]) => (
            <div key={key} style={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                key={`${value.baptism!!.baptismId}_churchName`}
              >
                <Typography sx={{ color: "#0103077F" }}>Church:</Typography>
                <Typography
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    color: "primary.main", // Use theme's primary color
                    textOverflow: "ellipsis",
                    textDecoration: "underline", // Add underline to mimic a link
                    cursor: "pointer", // Change the cursor to a pointer
                    "&:hover": {
                      color: "primary.dark", // Change color on hover
                      textDecoration: "none", // Remove underline on hover
                    },
                  }}
                  onClick={() =>
                    navigate(`/churches/${value.church?.churchId}`)
                  }
                >
                  {value.church!!.name}
                </Typography>
              </Box>
              <Box
                key={`${value.baptism!!.baptismId}_time`}
                sx={styles.innerBox}
              >
                <Typography sx={{ color: "#0103077F" }}>Time:</Typography>
                <Typography
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {humaneReadableTime(
                    value.baptism!!.time!!,
                    value.baptism!!.timezone!!,
                  )}
                </Typography>
              </Box>
              <Box
                key={`${value.baptism!!.baptismId}_action`}
                sx={styles.innerBox}
              >
                <Typography sx={{ color: "#0103077F" }}>Action:</Typography>
                <Button
                  size="small"
                  onClick={() => setUnregisterDialog(value)}
                  variant="outlined"
                >
                  Unregister
                </Button>
              </Box>
              <Divider
                orientation="horizontal"
                textAlign="left"
                sx={{ mt: 1, mb: 2 }}
              />
            </div>
          ))}
          <Button
            onClick={handleSignOut}
            variant="contained"
            sx={[styles.actionButton, styles.logoutButton]}
          >
            Sign Out
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={[styles.actionButton, styles.deleteButton]}
            onClick={() => setOpenDeleteUserDialog(true)}
          >
            Delete My Account
          </Button>
        </Box>
      </Container>
      <ConfirmDialog
        open={openDeleteUserDialog}
        onClose={() => setOpenDeleteUserDialog(false)}
        onConfirm={handleDeleteCurrentUser}
        title={`Delete ${attributes.given_name} ${attributes.family_name}?`}
        content={`Are you sure you want to delete the user with the following email address: ${attributes.email}?\n This action cannot be undone.`}
      />
      <ConfirmDialog
        open={!!unregisterDialog}
        onClose={() => setUnregisterDialog(undefined)}
        onConfirm={() => handleUnregisterForBaptism(unregisterDialog!)}
        title={`Remove Baptism Registration?`}
        content={`Are you sure you want to unregister for the baptism event at: ${unregisterDialog?.church?.name ?? ""}?`}
      />
    </>
  ) : (
    <LoginWindow />
  );
};

export default AccountPage;
